import React, { useState, useRef, useEffect } from "react";
import tourData from "../data/tourData.json";
import InfoBox from "./InfoBox";
import "./GermanyMap.css";

const GermanyMap = () => {
  const [selectedPin, setSelectedPin] = useState(null);
  const [hoveredPin, setHoveredPin] = useState(null);
  const [pinPosition, setPinPosition] = useState(null);
  const svgRef = useRef(null);

  // We'll use the external SVG file for Germany's outline

  // Convert lat/lng to SVG coordinates (matching germany_map.svg viewBox)
  const projectCoordinates = (lat, lng) => {
    // Germany bounds approximately:
    // North: 55.0°, South: 47.3°, East: 15.0°, West: 5.9°
    const bounds = {
      north: 55.0,
      south: 47.3,
      east: 15.0,
      west: 5.9,
    };

    // SVG viewBox dimensions from germany_map.svg: 0 0 591.504 800.504
    const width = 591.504;
    const height = 800.504;
    const padding = 30;

    // Convert to SVG coordinates with padding
    const x =
      padding +
      ((lng - bounds.west) / (bounds.east - bounds.west)) *
        (width - 2 * padding);
    const y =
      padding +
      ((bounds.north - lat) / (bounds.north - bounds.south)) *
        (height - 2 * padding);

    return { x, y };
  };

  // Handle pin click
  const handlePinClick = (dealer, event) => {
    if (selectedPin?.id === dealer.id) {
      setSelectedPin(null);
      setPinPosition(null);
    } else {
      setSelectedPin(dealer);

      // Calculate screen position of the pin
      const svgRect = svgRef.current.getBoundingClientRect();
      const coords = projectCoordinates(dealer.lat, dealer.lng);

      // Convert SVG coordinates to screen coordinates
      const scaleX = svgRect.width / 591.504;
      const scaleY = svgRect.height / 800.504;

      const screenX = svgRect.left + coords.x * scaleX;
      const screenY = svgRect.top + coords.y * scaleY;

      setPinPosition({ x: screenX, y: screenY });
    }
  };

  // Handle pin hover
  const handlePinHover = (dealer) => {
    setHoveredPin(dealer);
  };

  // Handle pin hover end
  const handlePinLeave = () => {
    setHoveredPin(null);
  };

  // Generate dots between two points
  const generateDots = (from, to) => {
    const fromCoords = projectCoordinates(from[0], from[1]);
    const toCoords = projectCoordinates(to[0], to[1]);

    // Calculate distance and number of dots
    const dx = toCoords.x - fromCoords.x;
    const dy = toCoords.y - fromCoords.y;
    const distance = Math.sqrt(dx * dx + dy * dy);
    const dotSpacing = 15; // Increased spacing - fewer dots
    const numDots = Math.floor(distance / dotSpacing);

    const dots = [];
    for (let i = 0; i <= numDots; i++) {
      const ratio = i / numDots;
      const x = fromCoords.x + dx * ratio;
      const y = fromCoords.y + dy * ratio;
      dots.push({ x, y, id: `${from[0]}-${from[1]}-${i}` });
    }

    return dots;
  };

  // Handle click outside to close info box
  useEffect(() => {
    const handleClickOutside = (event) => {
      // Don't close if clicking on the InfoBox tooltip or its children
      const isInfoBoxClick =
        event.target.closest(".info-box-tooltip") ||
        event.target.closest(".info-box");

      if (isInfoBoxClick) {
        console.log("Click detected on InfoBox - not closing");
        return;
      }

      // Close if clicking outside the SVG or InfoBox
      if (svgRef.current && !svgRef.current.contains(event.target)) {
        console.log("Click outside SVG and InfoBox - closing tooltip");
        setSelectedPin(null);
        setPinPosition(null);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  return (
    <div className="germany-map-container">
      <svg
        ref={svgRef}
        viewBox="0 0 591.504 800.504"
        className="germany-map-svg"
        preserveAspectRatio="xMidYMid meet"
      >
        {/* Germany outline as background image */}
        <image
          href="/germany_map.svg"
          x="0"
          y="0"
          width="591.504"
          height="800.504"
          className="germany-outline"
          opacity="0.8"
        />

        {/* Overlay rect for consistent styling */}
        <rect
          x="0"
          y="0"
          width="591.504"
          height="800.504"
          fill="none"
          stroke="none"
          className="germany-map-overlay"
        />

        {/* Route dots */}
        {tourData.routes.map((route) => {
          const dots = generateDots(route.fromCoords, route.toCoords);
          return (
            <g key={route.id} className="route-dots-group">
              {dots.map((dot) => (
                <circle
                  key={dot.id}
                  cx={dot.x}
                  cy={dot.y}
                  r="3"
                  fill="#FF6E00"
                  className="route-dot"
                  opacity="0.8"
                />
              ))}
            </g>
          );
        })}

        {/* Dealer pins */}
        {tourData.dealers.map((dealer, index) => {
          const coords = projectCoordinates(dealer.lat, dealer.lng);
          const isSelected = selectedPin?.id === dealer.id;
          const isHovered = hoveredPin?.id === dealer.id;

          return (
            <g key={dealer.id} className="dealer-pin-group">
              {/* Pin shadow */}
              <image
                href="/pin.svg"
                x={coords.x - 29.25}
                y={coords.y - 78.25}
                width="58.5"
                height="78"
                className="pin-shadow"
                opacity="0.2"
                transform="translate(2, 2)"
              />

              {/* Main pin icon */}
              <image
                href="/pin.svg"
                x={coords.x - 29.25}
                y={coords.y - 78.25}
                width="58.5"
                height="78"
                className={`dealer-pin ${isHovered ? "hovered" : ""} ${
                  isSelected === false && selectedPin ? "blurred" : ""
                }`}
                onClick={(event) => handlePinClick(dealer, event)}
                onMouseEnter={() => handlePinHover(dealer)}
                onMouseLeave={handlePinLeave}
                style={{
                  cursor: "pointer",
                }}
              />
            </g>
          );
        })}
      </svg>

      {/* Info box */}
      {selectedPin && (
        <InfoBox
          dealer={selectedPin}
          onClose={() => {
            setSelectedPin(null);
            setPinPosition(null);
          }}
          pinPosition={pinPosition}
        />
      )}
    </div>
  );
};

export default GermanyMap;
